import { useLayoutEffect, useRef, useState } from 'react';
import { currencySymbol, currencyName } from '../money';

/**
 * A MONEY INPUT THAT READS LIKE MONEY.
 *
 * The standing rule (Vinnie, 8 Oct 2026): "Any field that has money in it should
 * properly format into currency." So instead of a bare number box, this shows
 * the active currency symbol inside the field and thousands-separates the number
 * as it is typed — the same way money READS elsewhere (fmtMoney). Whole units
 * only, because fmtMoney rounds and the decision tier does not need pennies.
 *
 * The value is the NUMBER; `onChange` hands back the number, or `undefined` when
 * the field is cleared — an amount is only meaningful as a real number, so an
 * empty field clears rather than storing a zero (matches money.ts `asAmount`).
 *
 * The symbol is MEASURED, not estimated. A per-character pad guessed the width
 * and sat a multi-letter symbol (TT$, AED) under the digits; here the rendered
 * symbol's own width sets the input's left pad, so any symbol clears the number.
 *
 * The caret is PRESERVED across the live reformat. Re-inserting thousands commas
 * rewrites the string on every keystroke, which would otherwise throw the caret
 * to the end; we remember how many digits sat to its left and put it back after
 * the same digit once the reformatted value renders.
 *
 * Screen readers are told the currency: the symbol is decorative (aria-hidden),
 * and the input carries a label naming the currency, because "£" alone reads as
 * "pound sign", not an amount in pounds.
 */
export default function MoneyInput({
  value,
  onChange,
  placeholder,
  autoFocus,
  id,
  required,
  disabled,
  ariaLabel,
}: {
  value: number | undefined;
  onChange: (n: number | undefined) => void;
  placeholder?: string;
  autoFocus?: boolean;
  id?: string;
  required?: boolean;
  disabled?: boolean;
  ariaLabel?: string;
}) {
  const sym = currencySymbol();
  const shown =
    value == null || !Number.isFinite(value) ? '' : Math.round(value).toLocaleString();

  const inputRef = useRef<HTMLInputElement>(null);
  const symRef = useRef<HTMLSpanElement>(null);
  // How many digits sat to the left of the caret at the last keystroke — the
  // anchor the caret is restored to after the value reformats. null = don't move.
  const caretDigits = useRef<number | null>(null);
  const [padLeft, setPadLeft] = useState(24);

  // Measure the symbol so the number never sits under it, whatever the symbol.
  useLayoutEffect(() => {
    const w = symRef.current?.offsetWidth ?? 0;
    if (w) setPadLeft(Math.round(12 + w + 6));
  }, [sym]);

  // Restore the caret after the reformatted value has rendered.
  useLayoutEffect(() => {
    const el = inputRef.current;
    const want = caretDigits.current;
    caretDigits.current = null;
    if (!el || want == null || document.activeElement !== el) return;
    let seen = 0;
    let pos = shown.length;
    if (want === 0) {
      pos = 0;
    } else {
      for (let i = 0; i < shown.length; i++) {
        if (shown[i] >= '0' && shown[i] <= '9') {
          seen++;
          if (seen === want) { pos = i + 1; break; }
        }
      }
    }
    el.setSelectionRange(pos, pos);
  }, [shown]);

  const srLabel = ariaLabel ? `${ariaLabel}, in ${currencyName()}` : `Amount in ${currencyName()}`;

  return (
    <span className="money-input">
      <span className="money-input-sym" aria-hidden="true" ref={symRef}>
        {sym}
      </span>
      <input
        ref={inputRef}
        id={id}
        className="input money-input-field"
        inputMode="numeric"
        autoFocus={autoFocus}
        disabled={disabled}
        aria-required={required || undefined}
        aria-label={srLabel}
        placeholder={placeholder}
        value={shown}
        style={{ paddingLeft: `${padLeft}px` }}
        onChange={(e) => {
          const el = e.target;
          const raw = el.value;
          const caret = el.selectionStart ?? raw.length;
          let d = 0;
          for (let i = 0; i < caret; i++) if (raw[i] >= '0' && raw[i] <= '9') d++;
          caretDigits.current = d;
          const digits = raw.replace(/[^0-9]/g, '');
          onChange(digits === '' ? undefined : Number(digits));
        }}
      />
    </span>
  );
}
